import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import usePageBodyClass from "../utils/usePageBodyClass";
import ExamProctorWidget from "../components/ExamProctorWidget";
import "./Instructions.css";

const $ = window.$;

export default function Instructions() {
  usePageBodyClass("page-instructions");
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Session Guard Check
    if (localStorage.getItem("examStatus") !== "locked") {
      navigate("/login", { replace: true });
      return undefined;
    }

    // LIVE DATE & TIME CLOCK
    function updateLiveDateTime() {
      const now = new Date();
      const options = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      $("#liveDateTime").text(now.toLocaleDateString("en-US", options));
    }
    updateLiveDateTime();
    const clockTimer = setInterval(updateLiveDateTime, 1000);

    // Anti-cheat: block copy/selection (contextmenu / Escape / F12 / etc.
    // are already blocked globally by the exam shell).
    $(document).on(
      "selectstart.pageInstr copy.pageInstr paste.pageInstr cut.pageInstr dragstart.pageInstr",
      (e) => {
        e.preventDefault();
        return false;
      }
    );

    // Anti-Tab Switching Monitor
    let tabSwitchCount = 0;
    $(document).on("visibilitychange.pageInstr", function () {
      if (document.hidden && localStorage.getItem("examStatus") === "locked") {
        tabSwitchCount++;
        if (tabSwitchCount >= 2) {
          alert("Violation Limit Reached! Returning to Login Screen.");
          localStorage.removeItem("examStatus");
          navigate("/login", { replace: true });
        } else {
          alert(`WARNING (${tabSwitchCount}/2): Window switching is prohibited!`);
        }
      }
    });

    // Retrieve & Display Candidate Name
    const savedName = localStorage.getItem("candidateName");
    if (savedName) {
      $("#candidateNameDisplay").text(savedName);
      $("#studentNameText").text(savedName);
    }

    // Setup Timer & Auto-Redirection
    let totalSeconds = 10;

    function updateTimer() {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      const formattedMinutes = String(minutes).padStart(2, "0");
      const formattedSeconds = String(seconds).padStart(2, "0");

      $("#timerDisplay").html(
        `<i class="fa-regular fa-clock me-1"></i>${formattedMinutes}:${formattedSeconds}`
      );

      if (totalSeconds <= 0) {
        clearInterval(countdownInterval);
        $("#timerDisplay")
          .html('<i class="fa-solid fa-circle-check text-success me-1"></i>00:00')
          .css("color", "#16a34a");
        $("#timerStatus")
          .html('<i class="fa-solid fa-spinner fa-spin text-success me-1"></i>Redirecting to test environment...')
          .addClass("text-success")
          .removeClass("text-muted");

        // Direct automatic redirection
        navigate("/dashboard", { replace: true });
      } else {
        totalSeconds--;
      }
    }

    const countdownInterval = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(clockTimer);
      clearInterval(countdownInterval);
      $(document).off(".pageInstr");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="page-instructions">
      <ExamProctorWidget />

      <nav className="navbar navbar-dark navbar-portal shadow-sm">
        <div className="container">
          <span className="navbar-brand mb-0 h1">
            <i className="fa-solid fa-graduation-cap text-warning me-2"></i>Online Exam Portal
          </span>

          <div className="header-datetime d-none d-md-flex align-items-center">
            <i className="fa-regular fa-calendar-days me-2"></i>
            <span id="liveDateTime">Loading Date &amp; Time...</span>
          </div>

          <div className="text-white small">
            Candidate: <span id="candidateNameDisplay" className="text-warning fw-bold">Guest</span>
          </div>
        </div>
      </nav>

      <div className="container my-auto py-4">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="card instruction-card">
              <div className="card-header bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h3 className="mb-1">
                    Welcome, <span id="studentNameText" className="student-welcome">Candidate</span>!
                  </h3>
                  <p className="text-muted small mb-0">Please read all instructions carefully before the exam starts.</p>
                </div>

                <div className="timer-badge p-2 px-3 text-center">
                  <div className="small fw-bold text-uppercase">Auto Redirect In</div>
                  <div className="timer-text" id="timerDisplay">
                    <i className="fa-regular fa-clock me-1"></i>00:10
                  </div>
                </div>
              </div>

              <hr className="mx-4 my-2" />

              <div className="card-body px-4">
                <h5 className="text-danger fw-bold mb-3">
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>Important Guidelines &amp; Rules:
                </h5>

                <ol className="rules-list text-secondary ps-3">
                  <li>
                    <strong>Reading Time Mandatory:</strong> You have <strong>10 seconds</strong> to read these
                    rules. Once the timer reaches zero, you will be redirected automatically to the test
                    dashboard.
                  </li>
                  <li>
                    <strong>Exam Duration &amp; Format:</strong> The total test duration will begin immediately
                    upon redirection. All questions are multiple-choice.
                  </li>
                  <li>
                    <strong>Proctored Environment:</strong> Do not refresh the page, switch browser tabs,
                    minimize the window, or close the browser during the exam. Any attempt to navigate away
                    will result in immediate disqualification.
                  </li>
                  <li>
                    <strong>Device Requirements &amp; Peripheral Restrictions:</strong> Ensure you have a stable
                    internet connection. Use of external calculators, unauthorized secondary mobile devices,
                    secondary monitors, or copy-pasting code/text is strictly prohibited.
                  </li>
                  <li>
                    <strong>Inspection &amp; Developer Tools:</strong> Attempting to open Developer Console
                    (<kbd>F12</kbd>, <kbd>Ctrl+Shift+I</kbd>), inspect elements, or alter DOM elements will
                    trigger an automatic security lock and disqualify your attempt.
                  </li>
                  <li>
                    <strong>Navigation Lockdown:</strong> Screen lockdown will remain active throughout the test
                    session. You will only be permitted to exit lockdown upon reaching the final Summary screen
                    via the designated Exit button.
                  </li>
                  <li>
                    <strong>Auto-Submission:</strong> The test will automatically submit when the overall exam
                    duration expires, or if multiple window violation alerts are triggered, regardless of
                    unanswered questions.
                  </li>
                </ol>

                <div className="alert alert-warning mt-4 small" role="alert">
                  <i className="fa-solid fa-circle-info me-2"></i>
                  <strong>Note:</strong> Keep your webcam centered and ensure your face remains visible. Ensure
                  your microphone remains clear of background noise if proctoring is enabled by the
                  administrator.
                </div>
              </div>

              <div className="card-footer bg-light p-3 px-4 d-flex justify-content-between align-items-center">
                <span className="text-muted small" id="timerStatus">
                  <i className="fa-solid fa-lock me-1"></i>Exam will begin automatically when timer reaches
                  00:00.
                </span>
                <span className="badge bg-primary px-3 py-2">
                  <i className="fa-solid fa-spinner fa-spin me-1"></i>Preparing Exam...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
